/**
 * Claiming the OAuth popup window.
 *
 * `window.open` is only permitted while the browser still holds transient user
 * activation from the click that triggered it. Every OAuth entry point in the
 * dashboard has to ask `/api/v1/oauth/initiate` for the authorization URL
 * before it can open anything, and that round trip outlives the activation —
 * so opening the popup once the URL arrives is blocked, and each caller's
 * "popup was blocked" fallback then navigates the CURRENT tab instead.
 *
 * That is the bug as users report it: "it authorizes in the same tab, so I have
 * to go back to fastn". Nothing looks broken in the code — the popup call is
 * right there — which is why it survived in four call sites at once.
 *
 * packages/embed hit this first and fixed it exactly this way (see
 * `openConnectPopup` in packages/embed/src/headless/connect.ts, whose comment
 * records that awaiting first meant "the popup was blocked every time"). The
 * dashboard never picked the fix up. This module is that fix, shared, so the
 * two cannot drift again.
 *
 * Usage — the ordering is the whole point:
 *
 *   const popup = openOAuthPopup();          // synchronous, inside the click
 *   const { authorizationUrl } = await initiate();
 *   if (!navigateOAuthPopup(popup, authorizationUrl)) {
 *     window.location.href = authorizationUrl;   // genuinely blocked
 *   }
 *
 * An async function body runs synchronously up to its first `await`, so a call
 * placed above the first `await` in a click handler is still inside the
 * gesture. Anything below it is not.
 */

const POPUP_WIDTH = 620;
const POPUP_HEIGHT = 720;

/** Shared name so a second attempt reuses the window instead of stacking. */
export const OAUTH_POPUP_NAME = "fastn-oauth";

/**
 * Open a blank popup NOW, to be pointed at the authorization URL later.
 *
 * Must be called synchronously from the click handler — before any `await`.
 * Returns null when the browser blocked it, which is the caller's signal to
 * fall back to a same-tab redirect.
 */
/**
 * Where the user's OWN tab should return to after a same-tab OAuth redirect.
 *
 * Read by the bootstrap in main.tsx, by OAuthDonePage, and by the API-served
 * /api/v1/embed/oauth-done page, to answer one question: is this window the user's tab coming
 * back from a provider, or a popup that should close? Its PRESENCE is the whole signal, so a
 * leftover value does not degrade the answer - it inverts it.
 *
 * That is what broke connecting. A popup inherits a COPY of its opener's session storage at the
 * moment it is opened. The reasoning in main.tsx and App.tsx - that a popup cannot hold the
 * breadcrumb because it is written after the popup opens - holds for the CURRENT flow and not for
 * a previous one. Any abandoned same-tab redirect (popup blocked, then the user cancels at the
 * consent screen or navigates back) leaves the breadcrumb in the tab, and from then on every
 * popup opened from that tab carries a copy, decides it is the user's own tab, and
 * `location.replace`s itself to a URL from the earlier flow instead of closing. Measured in a
 * browser: the popup reads back the opener's value verbatim.
 *
 * The stored value is a PLAIN URL STRING and must stay one. `services/api/src/server.ts` reads
 * this key on its own /oauth-done page and passes it straight to `location.replace`, so wrapping
 * it in an envelope turns that return into a garbage relative path. An earlier draft of this fix
 * stored `{url, at}` for a staleness check and would have done exactly that.
 *
 * Staleness is deliberately NOT handled by a TTL. Expiring the breadcrumb strands the case it
 * exists for: on the blocked-popup fallback, a consent hop slower than the TTL (provider signup,
 * MFA, admin approval, a distracted user) leaves the user's own tab on "you can close this
 * window" with nowhere to go. Clearing on open is what fixes the reported bug, and it does so
 * without inventing a new way to lose someone.
 */
const RETURN_KEYS = ["fastn_oauth_return_url", "fastn_embed_return_url"] as const;

/** Written immediately before a same-tab redirect, and only on that branch. */
export function rememberOAuthReturn(url: string, key: (typeof RETURN_KEYS)[number] = "fastn_oauth_return_url"): void {
  try {
    sessionStorage.setItem(key, url);
  } catch {
    /* storage gone; the popup path does not need this */
  }
}

/** Consume the breadcrumb. Absent means "this is not the user's tab mid-redirect". */
export function takeOAuthReturn(): string | null {
  try {
    const url = sessionStorage.getItem(RETURN_KEYS[0]);
    if (url) sessionStorage.removeItem(RETURN_KEYS[0]);
    return url;
  } catch {
    return null;
  }
}

/**
 * Drop any breadcrumb left by an abandoned flow - BOTH keys.
 *
 * The embed key needs this as much as the dashboard one: the API's /oauth-done reads it first,
 * and the popup's own removeItem there mutates only the popup's inherited CLONE, so the opener's
 * copy survives every flow and is inherited by the next popup.
 */
function clearOAuthReturn(): void {
  for (const k of RETURN_KEYS) {
    try {
      sessionStorage.removeItem(k);
    } catch {
      /* storage gone */
    }
  }
}

/**
 * Open a blank popup NOW, to be pointed at the authorization URL later.
 *
 * Must be called synchronously from the click handler - before any `await`.
 * Returns null when the browser blocked it, which is the caller's signal to
 * fall back to a same-tab redirect.
 */
export function openOAuthPopup(): Window | null {
  if (typeof window === "undefined") return null;
  // BEFORE the popup exists: it inherits a copy of this tab's session storage, and a breadcrumb
  // left by an abandoned same-tab flow would make it mistake itself for the user's own tab.
  clearOAuthReturn();
  const left = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
  const top = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);
  try {
    return window.open(
      "about:blank",
      OAUTH_POPUP_NAME,
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable,scrollbars`,
    );
  } catch {
    return null;
  }
}

/**
 * Point a claimed popup at the authorization URL.
 *
 * Returns false when there is no usable window, so the caller can fall back.
 * Note the two false cases are NOT the same:
 *
 *   popup === null      blocked at open time — fall back to the current tab.
 *   popup.closed        the user closed it while /oauth/initiate was in flight.
 *                       Do NOT then hijack their tab; treat it as a cancel.
 *
 * `wasClaimed` below lets callers tell those apart.
 *
 * `location.replace` rather than `href`: the popup's history starts on
 * about:blank, and replacing it keeps Back inside the provider's own flow
 * instead of landing the user on a blank page.
 */
export function navigateOAuthPopup(popup: Window | null, url: string): boolean {
  if (!popup || popup.closed) return false;
  try {
    popup.location.replace(url);
    return true;
  } catch {
    // Defensive: an opener may always navigate a window it opened, but a
    // provider redirect landing first would make `replace` throw.
    try {
      popup.location.href = url;
      return true;
    } catch {
      return false;
    }
  }
}

/** True when a window was claimed but the user has since closed it. */
export function wasClaimedThenClosed(popup: Window | null): boolean {
  return !!popup && popup.closed;
}

/** Close a popup without caring whether it is already gone. */
export function closeOAuthPopup(popup: Window | null): void {
  try {
    popup?.close();
  } catch {
    /* already closed, or cross-origin after a provider redirect */
  }
}
